import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { FaExpandArrowsAlt } from 'react-icons/fa';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../styles/mapbox-popup-fix.css';
import '../styles/map-cursor-mix.css';
import '../styles/map-popups.css';
import PhotoModalWithAgent from '../components/PhotoModalWithAgent';
import DayFilterDropdown from '../components/DayFilterDropdown';
import DemoModal from '../components/DemoModal';
import AgentContactModal from '../components/AgentContactModal';
import { useDemoModal } from '../hooks/useDemoModal';
import { useDemoOpenHouses } from '../services/demoDataService';
import { generateDayFilterOptions, filterOpenHousesByDay } from '../utils/dayFilterUtils';
import { getGalleryImageUrl } from '../utils/imageUtils';
import analyticsService from '../services/analyticsService';
import { getAddressParts } from '../utils/addressUtils';

// Custom hook to manage photo modal state globally across map markers
function usePhotoModalState() {
  const [activeModalListing, setActiveModalListing] = useState(null);
  
  const showModal = (listing) => {
    setActiveModalListing(listing);
  };
  
  const hideModal = () => {
    setActiveModalListing(null);
  };
  
  return {
    activeModalListing,
    showModal,
    hideModal
  };
}

// Helper function to format open house date/time
const formatOpenHouseTime = (startTime, endTime) => {
  if (!startTime || !endTime) return 'Time TBD';
  
  const start = new Date(startTime);
  const end = new Date(endTime);
  
  // Format day of week and date
  const dayOfWeek = start.toLocaleDateString('en-US', { weekday: 'short' });
  const month = start.toLocaleDateString('en-US', { month: 'short' });
  const day = start.getDate();
  
  // Format times in compact format (10-12p instead of 10:00 AM-12:00 PM)
  const startHour = start.getHours();
  const endHour = end.getHours();
  const startMin = start.getMinutes();
  const endMin = end.getMinutes();
  
  // Convert to 12-hour format and remove leading zeros
  const formatHour = (hour) => {
    const h = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return h.toString();
  };
  
  // Get AM/PM suffix
  const getAmPm = (hour) => hour >= 12 ? 'pm' : 'am';
  
  // Format start time
  const startTimeStr = `${formatHour(startHour)}${startMin > 0 ? ':' + startMin.toString().padStart(2, '0') : ''}${getAmPm(startHour)}`;
  
  // Format end time
  const endTimeStr = `${formatHour(endHour)}${endMin > 0 ? ':' + endMin.toString().padStart(2, '0') : ''}${getAmPm(endHour)}`;
  
  return `${dayOfWeek} ${month} ${day} • ${startTimeStr}-${endTimeStr}`;
};

// Global function to handle time selection in map popups
window.handleTimeSelection = function(containerId, expandableId, newSelectedIndex) {
  const container = document.getElementById(containerId);
  const allTimes = JSON.parse(container.getAttribute('data-all-times'));
  const newTimeData = allTimes[newSelectedIndex];
  
  // Update selected index
  container.setAttribute('data-selected-index', newSelectedIndex);
  
  // Update main badge text
  const mainBadge = document.getElementById(expandableId + '-main');
  const mainText = mainBadge.querySelector('span:first-child');
  mainText.textContent = newTimeData.time;
  
  // Update host badge
  const hostBadge = mainBadge.closest('.mapboxgl-popup-content').querySelector('[data-host-badge]');
  if (hostBadge) {
    if (newTimeData.host && newTimeData.originalData?.hostMemberKey) {
      hostBadge.style.display = 'flex';
      hostBadge.querySelector('[data-host-name]').textContent = newTimeData.host;
      
      // Update click functionality
      hostBadge.onclick = function(event) {
        event.stopPropagation();
        window.openAgentModalFromMap(newTimeData.originalData.hostMemberKey);
      };
      
      // Update cursor and hover effects
      hostBadge.style.cursor = 'pointer';
      hostBadge.onmouseover = function() {
        this.style.backgroundColor = 'rgba(0, 0, 0, 0.75)';
      };
      hostBadge.onmouseout = function() {
        this.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
      };
    } else {
      hostBadge.style.display = 'none';
      // Remove click functionality when no host
      hostBadge.onclick = null;
      hostBadge.style.cursor = 'default';
      hostBadge.onmouseover = null;
      hostBadge.onmouseout = null;
    }
  }
  
  // Close dropdown and clean up
  const expandable = document.getElementById(expandableId);
  const icon = document.getElementById(expandableId + '-icon');
  expandable.style.maxHeight = '0px';
  icon.style.transform = 'rotate(0deg)';
  
  // Clear the dropdown content to prevent any visual remnants
  setTimeout(() => {
    expandable.innerHTML = '';
  }, 300); // Wait for transition to complete
};

// Global function to open agent modal from map popup
window.openAgentModalFromMap = function(memberKey) {
  // This will be set by the component when it mounts
  if (window.demoMapAgentModal) {
    window.demoMapAgentModal({ show: true, memberKey });
  }
};

// Global function to rebuild and open dropdown
window.rebuildTimeDropdown = function(containerId, expandableId) {
  const container = document.getElementById(containerId);
  const el = document.getElementById(expandableId);
  const icon = document.getElementById(expandableId + '-icon');
  
  if (el.style.maxHeight === '0px' || el.style.maxHeight === '') {
    // Get current selected index
    const selectedIndex = parseInt(container.getAttribute('data-selected-index'));
    const allTimes = JSON.parse(container.getAttribute('data-all-times'));
    
        // Rebuild dropdown with all times EXCEPT the selected one
        const filteredTimes = allTimes.filter((timeData, idx) => idx !== selectedIndex);
        const dropdownHTML = filteredTimes
          .map((timeData, idx) => {
            const isFirst = idx === 0;
            return `
              <div 
                onclick="handleTimeSelection('${containerId}', '${expandableId}', ${allTimes.indexOf(timeData)})"
                style="
                  padding: 4px 8px;
                  border-radius: 4px;
                  font-size: 12px;
                  font-weight: bold;
                  background-color: rgba(255, 255, 255, 0.95);
                  backdrop-filter: blur(20px);
                  -webkit-backdrop-filter: blur(20px);
                  color: #1d1d1f;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                  white-space: nowrap;
                  margin-top: ${isFirst ? '8px' : '0'};
                  margin-bottom: 8px;
                  cursor: pointer;
              transition: background-color 0.15s ease, transform 0.1s ease;
              user-select: none;
              display: flex;
              align-items: center;
            "
            onmouseover="this.style.backgroundColor='rgba(0, 67, 255, 0.08)'; this.style.transform='translateX(2px)';"
            onmouseout="this.style.backgroundColor='rgba(255, 255, 255, 0.95)'; this.style.transform='translateX(0)';"
          >
            <span>${timeData.time}</span>
          </div>
        `;
      })
      .filter(html => html !== '')
      .join('');
    
    el.innerHTML = dropdownHTML;
    
    // Remove last margin-bottom
    const lastChild = el.lastElementChild;
    if (lastChild) {
      lastChild.style.marginBottom = '0';
    }
    
    // Open dropdown
    el.style.maxHeight = el.scrollHeight + 'px';
    icon.style.transform = 'rotate(180deg)';
  } else {
    // Close dropdown and clean up
    el.style.maxHeight = '0px';
    icon.style.transform = 'rotate(0deg)';
    
    // Clear the dropdown content to prevent any visual remnants
    setTimeout(() => {
      el.innerHTML = '';
    }, 300); // Wait for transition to complete
  }
};

// Helper function to create popup HTML content for open houses
const createOpenHousePopupHtml = (openHouse, allOpenHousesForProperty = []) => {
  // Extract essential data with proper fallbacks
  const photoUrl = openHouse.photo || '';
  const price = openHouse.price ? `$${openHouse.price.toLocaleString()}` : 'Price unavailable';
  const beds = openHouse.beds || '--';
  const baths = openHouse.baths || '--';
  const sqft = openHouse.sqft ? openHouse.sqft.toLocaleString() : '--';
  // Parse address correctly using improved parsing
  const addressParts = getAddressParts(openHouse);
  const address = addressParts.streetAddress;
  const cityStateZip = addressParts.cityStateZip;
  const agentName = openHouse.agentName || '';
  const hostDisplayName = openHouse.hostDisplayName || '';
  const listingId = openHouse.listingId || '';
  const neighborhood = (openHouse.subdivisionName && openHouse.subdivisionName !== 'N/A') ? openHouse.subdivisionName : (openHouse.city || '');
  
  // Get all open house times for this property
  const allOpenHouses = allOpenHousesForProperty.length > 0 ? allOpenHousesForProperty : [openHouse];
  
  // Sort by start time chronologically (earliest first), then format
  const sortedOpenHouses = allOpenHouses
    .filter(oh => oh.openHouseStartTime && oh.openHouseEndTime)
    .sort((a, b) => new Date(a.openHouseStartTime) - new Date(b.openHouseStartTime));
  
  const openHouseTimes = sortedOpenHouses
    .map(oh => formatOpenHouseTime(oh.openHouseStartTime, oh.openHouseEndTime))
    .filter(time => time !== 'Time TBD');

  // Camera icon and photo count (use same logic as card: count array length)
  const photoCount = Array.isArray(openHouse.photos) ? openHouse.photos.length : 0;
  const cameraIconSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="white" viewBox="0 0 16 16" style="margin-right: 4px;">
      <path d="M15 12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h1.172a3 3 0 0 0 2.12-.879l.83-.828A1 1 0 0 1 6.827 3h2.344a1 1 0 0 1 .707.293l.828.828A3 3 0 0 0 12.828 5H14a1 1 0 0 1 1 1v6zM2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828-.828A2 2 0 0 1 3.172 4H2z"/>
      <path d="M8 11a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm0-4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"/>
    </svg>
  `;
  const photoCountBadgeHtml = photoCount > 0 ? `
      <div 
        style="position: absolute; bottom: 8px; right: 8px; background-color: rgba(0, 0, 0, 0.6); color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px; display: flex; align-items: center; z-index: 1; cursor: pointer;"
        data-photo-badge="true"
      >
        ${cameraIconSvg}${photoCount}
      </div>
  ` : '';

  return `
    <div style="
      width: 280px;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      overflow: hidden;
      background-color: #fff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      font-family: 'Montserrat', Arial, system-ui, sans-serif;
    ">
      <div style="position: relative; width: 100%; padding-bottom: 65%; margin: 0; background: #f0f0f0; overflow: hidden; border-radius: 8px 8px 0 0;">
        ${photoUrl ? `
          <img 
            src="${getGalleryImageUrl(photoUrl)}" 
            alt="${address}" 
            style="
              position: absolute;
              width: 100%;
              height: 100%;
              object-fit: cover;
              top: 0;
              left: 0;
              cursor: pointer;
              margin: 0;
              padding: 0;
              border: none;
              display: block;
            "
            class="map-popup-photo"
          />` : 
          `<div style="position: absolute; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #888; font-size: 14px;">No image</div>`
        }
        <!-- Expandable Open House Times Badge -->
        ${(() => {
          // Sort times and get all open house data
          const sortedOpenHouses = allOpenHousesForProperty
            .filter(oh => oh.openHouseStartTime && oh.openHouseEndTime)
            .sort((a, b) => {
              const startTimeDiff = new Date(a.openHouseStartTime) - new Date(b.openHouseStartTime);
              if (startTimeDiff !== 0) {
                return startTimeDiff;
              }
              // If start times are equal, sort by end time (earliest first)
              return new Date(a.openHouseEndTime) - new Date(b.openHouseEndTime);
            });
          
          if (sortedOpenHouses.length === 0) return '';
          
          const hasMultipleTimes = sortedOpenHouses.length > 1;
          const expandableId = `times-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          
          // Store ALL times with their data
          const allTimesData = JSON.stringify(sortedOpenHouses.map(oh => ({
            time: formatOpenHouseTime(oh.openHouseStartTime, oh.openHouseEndTime),
            host: oh.hostDisplayName || null,
            openHouseStartTime: oh.openHouseStartTime,
            openHouseEndTime: oh.openHouseEndTime,
            agentName: oh.agentName || null,
            // Include any other time-specific data that might be needed
            originalData: oh
          })));
          
          return `
            <div 
              id="${expandableId}-container"
              data-all-times='${allTimesData.replace(/'/g, "&apos;")}'
              data-selected-index="0"
              style="
                position: absolute;
                top: 8px;
                left: 8px;
                max-width: calc(100% - 16px);
                z-index: 2;
              "
            >
              <!-- Main time badge -->
              <div 
                id="${expandableId}-main"
                onclick="${hasMultipleTimes ? `rebuildTimeDropdown('${expandableId}-container', '${expandableId}')` : ''}"
                style="
                  padding: 4px 8px;
                  border-radius: 4px;
                  font-size: 12px;
                  font-weight: bold;
                  background-color: rgba(0, 0, 0, 0.7);
                  color: white;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  cursor: ${hasMultipleTimes ? 'pointer' : 'default'};
                  user-select: none;
                  transition: background-color 0.2s ease;
                  max-width: calc(100% - 8px);
                  overflow: hidden;
                "
                ${hasMultipleTimes ? `
                  onmouseover="this.style.backgroundColor='rgba(0, 0, 0, 0.85)'"
                  onmouseout="this.style.backgroundColor='rgba(0, 0, 0, 0.75)'"
                ` : ''}
              >
                <span style="white-space: nowrap;">
                  ${formatOpenHouseTime(sortedOpenHouses[0].openHouseStartTime, sortedOpenHouses[0].openHouseEndTime)}
                </span>
                
                ${hasMultipleTimes ? `
                  <span style="
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    flex-shrink: 0;
                  ">
                    <span style="
                      font-size: 9px;
                      font-weight: 500;
                      opacity: 0.9;
                    ">
                      +${sortedOpenHouses.length - 1}
                    </span>
                    <svg 
                      id="${expandableId}-icon"
                      width="10" 
                      height="10" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="white" 
                      stroke-width="3" 
                      stroke-linecap="round" 
                      stroke-linejoin="round" 
                      style="
                        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        margin-top: 1px;
                      "
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </span>
                ` : ''}
              </div>
              
              ${hasMultipleTimes ? `
                <!-- Expandable section (dynamically populated) -->
                <div 
                  id="${expandableId}"
                style="
                  max-height: 0;
                  overflow: hidden;
                  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                "
                >
                </div>
              ` : ''}
            </div>
          `;
        })()}
        ${photoCountBadgeHtml}
        ${hostDisplayName ? `
          <div 
            data-host-badge
            onclick="${openHouse.hostMemberKey ? `event.stopPropagation(); openAgentModalFromMap('${openHouse.hostMemberKey}')` : ''}"
            style="
              position: absolute; 
              bottom: 8px; 
              left: 8px; 
              background-color: rgba(0, 0, 0, 0.6); 
              color: white; 
              padding: 4px 8px; 
              border-radius: 4px; 
              font-size: 12px; 
              z-index: 1; 
              max-width: calc(100% - 80px); 
              white-space: nowrap; 
              overflow: hidden; 
              text-overflow: ellipsis;
              display: flex;
              align-items: center;
              gap: 4px;
              cursor: ${openHouse.hostMemberKey ? 'pointer' : 'default'};
              transition: background-color 0.2s ease;
            "
            ${openHouse.hostMemberKey ? `
              onmouseover="this.style.backgroundColor='rgba(0, 0, 0, 0.75)'"
              onmouseout="this.style.backgroundColor='rgba(0, 0, 0, 0.6)'"
            ` : ''}
          >
            <span>Host:</span>
            <span data-host-name>${hostDisplayName}</span>
          </div>
        ` : `
          <!-- Hidden host badge placeholder (shows when user selects time with host) -->
          <div 
            data-host-badge
            style="
              position: absolute; 
              bottom: 8px; 
              left: 8px; 
              background-color: rgba(0, 0, 0, 0.6); 
              color: white; 
              padding: 4px 8px; 
              border-radius: 4px; 
              font-size: 12px; 
              z-index: 1; 
              max-width: calc(100% - 80px); 
              white-space: nowrap; 
              overflow: hidden; 
              text-overflow: ellipsis;
              display: none;
              align-items: center;
              gap: 4px;
            "
          >
            <span>Host:</span>
            <span data-host-name></span>
          </div>
        `}
      </div>
      <div style="padding: 16px; display: flex; flex-direction: column; flex-grow: 1;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <div style="font-size: 22px; font-weight: 600; color: #333; font-variant-numeric: tabular-nums;">${price}</div>
          ${listingId ? `<div style=\"font-size: 11px; color: #555; margin-left: 10px; white-space: nowrap; line-height: 1;\">MLS # ${listingId}</div>` : ''}
        </div>
        <div style="display: flex; align-items: center; color: #444; margin-bottom: 12px; height: 24px;">
          <div style="display: flex; align-items: center; font-size: 16px;">
            <span style="font-weight: 600;">${beds}</span>&nbsp;bed
            <span style="margin: 0 4px; color: #555;">·</span>
            <span style="font-weight: 600;">${baths}</span>&nbsp;bath
            <span style="margin: 0 4px; color: #555;">·</span>
            <span style="font-weight: 600;">${sqft}</span>&nbsp;sqft
          </div>
        </div>
        <div style="margin-bottom: 8px; overflow: hidden;">
          <div style="font-size: 16px; font-weight: 500; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: left; direction: ltr;" title="${address}">
            ${address}
          </div>
          <div style="font-size: 14px; color: #666; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: left; direction: ltr;">
            ${cityStateZip}
          </div>
        </div>
        ${neighborhood ? `
          <div style=\"font-size: 14px; color: #555; display: flex; align-items: center; height: 24px; margin: 2px 0 0 0;\">
            <svg width=\"16\" height=\"16\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\" style=\"margin-right: 4px; flex-shrink: 0;\"><path d=\"M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z\" fill=\"#555\"/></svg>
            <span style=\"word-wrap: break-word; overflow-wrap: break-word;\">${neighborhood}</span>
          </div>
        ` : ''}
        ${agentName ? `
          <div style=\"font-size: 14px; color: #666; display: flex; align-items: center; height: 20px; margin-top: 4px;\">
            <svg 
              width=\"16\" 
              height=\"16\" 
              viewBox=\"0 0 24 24\" 
              fill=\"none\" 
              xmlns=\"http://www.w3.org/2000/svg\" 
              style=\"margin-right: 4px; flex-shrink: 0; cursor: ${openHouse.raw?.ListAgentKey ? 'pointer' : 'default'}; transition: opacity 0.2s ease;\"
              onclick=\"${openHouse.raw?.ListAgentKey ? `openAgentModalFromMap('${openHouse.raw.ListAgentKey}')` : ''}\"
              ${openHouse.raw?.ListAgentKey ? `
                onmouseover=\"this.style.opacity='0.7';\"
                onmouseout=\"this.style.opacity='1';\"
              ` : ''}
            >
              <path d=\"M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z\" fill=\"#666\"/>
            </svg>
            <span 
              onclick=\"${openHouse.raw?.ListAgentKey ? `openAgentModalFromMap('${openHouse.raw.ListAgentKey}')` : ''}\"
              style=\"word-wrap: break-word; overflow-wrap: break-word; cursor: ${openHouse.raw?.ListAgentKey ? 'pointer' : 'default'}; color: #666; text-decoration: none; transition: color 0.2s ease;\"
              ${openHouse.raw?.ListAgentKey ? `
                onmouseover=\"this.style.color='#0043ff'; this.style.textDecoration='underline';\"
                onmouseout=\"this.style.color='#666'; this.style.textDecoration='none';\"
              ` : ''}
            >${agentName}</span>
          </div>
        ` : ''}
      </div>
    </div>
  `;
};

// Set Mapbox token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

function DemoMapView({ isMobile, currentView }) {
  const { openHouses, loading, error } = useDemoOpenHouses();
  const [selectedDay, setSelectedDay] = useState('all');
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const selectedMarkerIdRef = useRef(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const { showDemoModal, closeDemoModal } = useDemoModal(currentView);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [selectedAgentKey, setSelectedAgentKey] = useState(null);
  
  // Keep ref in sync with state
  useEffect(() => {
    selectedMarkerIdRef.current = selectedMarkerId;
  }, [selectedMarkerId]);

  // Register global agent modal callback
  useEffect(() => {
    window.demoMapAgentModal = ({ show, memberKey }) => {
      setShowAgentModal(show);
      setSelectedAgentKey(memberKey);
    };

    return () => {
      window.demoMapAgentModal = null;
    };
  }, []);
  const [previousSelectedDay, setPreviousSelectedDay] = useState('all');
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const popupRef = useRef(null);
  const originalMapViewRef = useRef(null);
  const mapViewRootRef = useRef(null); // Ref for the root div of MapView
  const mapDomContainerRef = useRef(null); // Ref for the actual map container div
  const { activeModalListing, showModal: originalShowModal, hideModal: originalHideModal } = usePhotoModalState();
  const isPhotoModalOpenRef = useRef(false);
  const pendingCenterRef = useRef(null);
  const pendingMarkerIdRef = useRef(null);
  // When true, we are programmatically closing the popup (e.g., due to filter change)
  // and should not restore the previous camera position in the popup close handler
  const suppressRestoreOnPopupCloseRef = useRef(false);

  // Generate day filter options based on available open houses
  const dayFilterOptions = useMemo(() => {
    return generateDayFilterOptions(openHouses);
  }, [openHouses]);

  // Function to get marker color based on hover and selection state
  const getMarkerColor = useCallback((markerId) => {
    const isSelected = selectedMarkerId === markerId;
    const isHovered = hoveredMarkerId === markerId;
    
    // Check if photo modal is open for this specific marker's listing
    const isModalOpenForThisMarker = isPhotoModalOpenRef.current && 
      activeModalListing && 
      selectedMarkerId === markerId;
    
    // If this marker is selected, ignore hover state to prevent blue pin when cursor stays on it
    const shouldIgnoreHover = isSelected;
    
    const color = (isSelected || (!shouldIgnoreHover && isHovered) || isModalOpenForThisMarker) ? '#0043ff' : '#ff1200';
    return color;
  }, [hoveredMarkerId, activeModalListing, selectedMarkerId]);

  // Function to update marker color
  const updateMarkerColor = useCallback((marker, markerId) => {
    const newColor = getMarkerColor(markerId);
    // Prefer Mapbox's API when available; fallback to direct SVG manipulation
    if (typeof marker.setColor === 'function') {
      marker.setColor(newColor);
      return;
    }

    const markerElement = marker.getElement();
    const svgElement = markerElement.querySelector('svg');
    if (svgElement) {
      const pathElement = svgElement.querySelector('path');
      if (pathElement) {
        pathElement.setAttribute('fill', newColor);
      }
    }
  }, [getMarkerColor]);

  // Filter open houses by selected day
  const filteredOpenHouses = useMemo(() => {
    return filterOpenHousesByDay(openHouses, selectedDay);
  }, [openHouses, selectedDay]);

  // Track when day filter changes and reset user interaction flag to allow recentering
  useEffect(() => {
    if (selectedDay !== previousSelectedDay) {
      setHasUserInteracted(false); // Allow recentering when day changes
      setPreviousSelectedDay(selectedDay);
    }
  }, [selectedDay, previousSelectedDay]);

  // Wrapped modal handlers to track modal state
  const savedCameraStateRef = useRef(null);
  
  const showModal = useCallback((listing) => {
    // Track map marker interaction
    analyticsService.trackMapInteraction('Marker Photo Click', {
      propertyId: listing?.listingId || listing?.listingKey,
      address: listing?.address
    });
    
    // Save exact camera state before opening modal
    if (mapRef.current) {
      savedCameraStateRef.current = {
        center: mapRef.current.getCenter(),
        zoom: mapRef.current.getZoom(),
        bearing: mapRef.current.getBearing(),
        pitch: mapRef.current.getPitch()
      };
      
      // Set flag to indicate modal is opening
      isPhotoModalOpenRef.current = true;
      
      // Open the modal
      originalShowModal(listing);
      
      // Force map back to saved position in the next animation frame
      requestAnimationFrame(() => {
        if (mapRef.current && savedCameraStateRef.current) {
          mapRef.current.jumpTo({
            center: savedCameraStateRef.current.center,
            zoom: savedCameraStateRef.current.zoom,
            bearing: savedCameraStateRef.current.bearing,
            pitch: savedCameraStateRef.current.pitch
          });
        }
      });
    } else {
      isPhotoModalOpenRef.current = true;
      originalShowModal(listing);
    }
  }, [originalShowModal]);

  const hideModal = useCallback(() => {
    // Hide the modal
    originalHideModal();
    
    // Force map back to saved position in the next animation frame
    requestAnimationFrame(() => {
      if (mapRef.current && savedCameraStateRef.current) {
        mapRef.current.jumpTo({
          center: savedCameraStateRef.current.center,
          zoom: savedCameraStateRef.current.zoom,
          bearing: savedCameraStateRef.current.bearing,
          pitch: savedCameraStateRef.current.pitch
        });
      }
    });
    
    // Only update the flag after ensuring map position
    isPhotoModalOpenRef.current = false;
  }, [originalHideModal]);

  const setupPopupPhotoClickListener = useCallback((popupInstance, listingForModal) => {
    const popupElement = popupInstance.getElement();
    if (!popupElement) return;
    const photoImg = popupElement.querySelector('img.map-popup-photo');
    if (photoImg && !photoImg.dataset.listenerAttached) {
      const clickHandler = (e) => {
        e.stopPropagation();
        
        // Close any open dropdowns before opening photo modal
        const timeContainer = popupElement.querySelector('[id^="times-"][id$="-container"]');
        if (timeContainer) {
          const expandableId = timeContainer.id.replace('-container', '');
          const expandable = document.getElementById(expandableId);
          const icon = document.getElementById(expandableId + '-icon');
          if (expandable && icon) {
            expandable.style.maxHeight = '0px';
            icon.style.transform = 'rotate(0deg)';
            // Clear the dropdown content to prevent any visual remnants
            setTimeout(() => {
              expandable.innerHTML = '';
            }, 300);
          }
        }
        
        // Get the currently selected time data from the dropdown
        if (timeContainer) {
          const selectedIndex = parseInt(timeContainer.getAttribute('data-selected-index')) || 0;
          const allTimes = JSON.parse(timeContainer.getAttribute('data-all-times') || '[]');
          
          if (allTimes.length > 0 && allTimes[selectedIndex]) {
            const selectedTimeData = allTimes[selectedIndex];
            
            // Create a new listing object with the selected time's data
            const updatedListing = {
              ...listingForModal,
              // Update with selected time's specific data
              hostDisplayName: selectedTimeData.host || listingForModal.hostDisplayName,
              agentName: selectedTimeData.agentName || listingForModal.agentName,
              openHouseStartTime: selectedTimeData.openHouseStartTime || listingForModal.openHouseStartTime,
              openHouseEndTime: selectedTimeData.openHouseEndTime || listingForModal.openHouseEndTime,
              // Use the original data from the selected time if available
              ...(selectedTimeData.originalData ? {
                hostDisplayName: selectedTimeData.originalData.hostDisplayName,
                agentName: selectedTimeData.originalData.agentName,
                openHouseStartTime: selectedTimeData.originalData.openHouseStartTime,
                openHouseEndTime: selectedTimeData.originalData.openHouseEndTime
              } : {})
            };
            
            showModal(updatedListing);
          } else {
            // Fallback to original listing if no time data found
            showModal(listingForModal);
          }
        } else {
          // Fallback to original listing if no time container found
          showModal(listingForModal);
        }
      };
      photoImg.addEventListener('click', clickHandler);
      photoImg.dataset.listenerAttached = 'true';
    }
    
    // Add click handler for photo count badge
    const photoBadge = popupElement.querySelector('[data-photo-badge="true"]');
    if (photoBadge && !photoBadge.dataset.listenerAttached) {
      const badgeClickHandler = (e) => {
        e.stopPropagation();
        showModal(listingForModal);
      };
      photoBadge.addEventListener('click', badgeClickHandler);
      photoBadge.dataset.listenerAttached = 'true';
    }
  }, [showModal]);

  // Helper function to calculate popup anchor based on marker position
  // Prefers positioning towards map center
  const calculatePopupAnchor = useCallback((markerLngLat) => {
    if (!mapRef.current) return { anchor: 'top', offset: 25 };
    
    const map = mapRef.current;
    const mapContainer = map.getContainer();
    const mapRect = mapContainer.getBoundingClientRect();
    const markerPoint = map.project(markerLngLat);
    
    // Account for overlay controls
    const CONTROL_BUFFER_TOP = 70;
    const CONTROL_BUFFER_RIGHT = 170;
    const CONTROL_BUFFER_BOTTOM = 30;
    const CONTROL_BUFFER_LEFT = 30;
    
    const estimatedPopupHeight = 400;
    const estimatedPopupWidth = 300;
    
    // Calculate center of visible map area
    const centerX = mapRect.width / 2;
    const centerY = mapRect.height / 2;
    
    // Calculate available space in each direction
    const spaceAbove = markerPoint.y - CONTROL_BUFFER_TOP;
    const spaceBelow = mapRect.height - markerPoint.y - CONTROL_BUFFER_BOTTOM;
    const spaceLeft = markerPoint.x - CONTROL_BUFFER_LEFT;
    const spaceRight = mapRect.width - markerPoint.x - CONTROL_BUFFER_RIGHT;
    
    // Determine which direction is towards center
    const markerIsLeftOfCenter = markerPoint.x < centerX;
    const markerIsAboveCenter = markerPoint.y < centerY;
    
    // Prefer positioning towards center
    let anchor, offset;
    
    // Vertical preference (towards center)
    if (markerIsAboveCenter && spaceBelow >= estimatedPopupHeight) {
      // Marker is in top half, open below (towards center)
      anchor = 'top';
      offset = 25;
    } else if (!markerIsAboveCenter && spaceAbove >= estimatedPopupHeight) {
      // Marker is in bottom half, open above (towards center)
      anchor = 'bottom';
      offset = [0, -25];
    }
    // Horizontal preference if vertical doesn't work
    else if (markerIsLeftOfCenter && spaceRight >= estimatedPopupWidth) {
      // Marker is on left, open right (towards center)
      anchor = 'left';
      offset = 25;
    } else if (!markerIsLeftOfCenter && spaceLeft >= estimatedPopupWidth) {
      // Marker is on right, open left (towards center)
      anchor = 'right';
      offset = [-25, 0];
    }
    // Fallback to any available space
    else if (spaceBelow >= estimatedPopupHeight) {
      anchor = 'top';
      offset = 25;
    } else if (spaceAbove >= estimatedPopupHeight) {
      anchor = 'bottom';
      offset = [0, -25];
    } else if (spaceRight >= estimatedPopupWidth) {
      anchor = 'left';
      offset = 25;
    } else if (spaceLeft >= estimatedPopupWidth) {
      anchor = 'right';
      offset = [-25, 0];
    } else {
      // Last resort
      anchor = 'top';
      offset = 25;
    }
    
    return { anchor, offset };
  }, []);

  // Initialize map
  useEffect(() => {
    // Check if map container exists
    const mapContainer = document.getElementById('open-house-map-view');
    if (!mapContainer) {
      console.error('Map container #open-house-map-view not found');
      return;
    }

    // Initialize map only if it hasn't been initialized yet
    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapDomContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-121.3153, 44.0582],
      zoom: 10,
      attributionControl: true,
      scrollZoom: true,
      boxZoom: true,
      dragRotate: false,
      dragPan: true,
      keyboard: true,
      doubleClickZoom: true,
      touchZoomRotate: true
    });

    // Add navigation controls (zoom, compass) - only on desktop
    if (!isMobile) {
      const navControl = new mapboxgl.NavigationControl({ showCompass: false });
      map.addControl(navControl, 'bottom-right');
      
      // Add blur event listeners to zoom control buttons after they're rendered
      setTimeout(() => {
        // Try multiple possible selectors for zoom controls
        const zoomInButton = map.getContainer().querySelector('.mapboxgl-ctrl-zoom-in') || 
                            map.getContainer().querySelector('[aria-label="Zoom in"]') ||
                            map.getContainer().querySelector('button[title="Zoom in"]');
        const zoomOutButton = map.getContainer().querySelector('.mapboxgl-ctrl-zoom-out') || 
                             map.getContainer().querySelector('[aria-label="Zoom out"]') ||
                             map.getContainer().querySelector('button[title="Zoom out"]');
        
        
        if (zoomInButton) {
          zoomInButton.addEventListener('click', (e) => {
            setTimeout(() => {
              e.target.blur();
              e.target.style.outline = 'none';
            }, 0);
          });
        }
        
        if (zoomOutButton) {
          zoomOutButton.addEventListener('click', (e) => {
            setTimeout(() => {
              e.target.blur();
              e.target.style.outline = 'none';
            }, 0);
          });
        }
      }, 200);
    }
    
    mapRef.current = map;

    // Add map click handler to close popups and clear hover state
    map.on('click', () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      // Clear hover state when clicking on map
      setHoveredMarkerId(null);
      setSelectedMarkerId(null);
      selectedMarkerIdRef.current = null;
    });

    // Track user interaction to prevent auto-fitBounds after user has zoomed/panned
    map.on('zoom', () => setHasUserInteracted(true));
    map.on('pan', () => setHasUserInteracted(true));
    map.on('move', () => setHasUserInteracted(true));

    // Cleanup on unmount
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [isMobile]);

  // Update markers when open houses change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !filteredOpenHouses?.length) return;
    
    // Remove existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    
    // Create a bounds object to encompass all open houses
    const bounds = new mapboxgl.LngLatBounds();
    
    // Group open houses by property (listingKey)
    const groupedOpenHouses = {};
    filteredOpenHouses.forEach(openHouse => {
      const lat = parseFloat(openHouse.latitude);
      const lon = parseFloat(openHouse.longitude);
      if (isNaN(lat) || isNaN(lon)) return;

      const propertyKey = openHouse.listingKey || `${lat}-${lon}`;
      if (!groupedOpenHouses[propertyKey]) {
        groupedOpenHouses[propertyKey] = {
          openHouses: [],
          lat,
          lon
        };
      }
      groupedOpenHouses[propertyKey].openHouses.push(openHouse);
    });

    // Create markers for each property (one marker per property, showing all open house times)
    Object.values(groupedOpenHouses).forEach(propertyGroup => {
      const { openHouses, lat, lon } = propertyGroup;
      const primaryOpenHouse = openHouses[0]; // Use first open house for property details

      // Extend bounds to include this property
      bounds.extend([lon, lat]);

      // Create unique marker ID for this property
      const markerId = `marker-${primaryOpenHouse.listingKey || `${lat}-${lon}`}`;
      
      // Use default red color for initial creation
      const markerColor = '#ff1200';

      const marker = new mapboxgl.Marker({ color: markerColor, scale: 0.8 })
        .setLngLat([lon, lat]);
      
      // Store marker ID on the marker element for color updates
      marker.getElement().dataset.markerId = markerId;

      // Create popup with mobile-appropriate positioning
      let popup;
      
      if (isMobile) {
        // For mobile, use simple positioning that works well after panning
        popup = new mapboxgl.Popup({
          offset: 25, // Always position below marker on mobile
          anchor: 'top',
          closeButton: false,
          closeOnClick: true,
          maxWidth: '280px',
          className: 'custom-mapbox-popup map-view-popup',
          closeOnMove: false
        });
      } else {
        // For desktop, use smart positioning based on available space
        const { anchor, offset } = calculatePopupAnchor([lon, lat]);
        
        popup = new mapboxgl.Popup({
          offset: offset,
          anchor: anchor,
          closeButton: false,
          closeOnClick: true,
          maxWidth: '280px',
          className: 'custom-mapbox-popup map-view-popup',
          closeOnMove: false
        });
      }
      
      popup
        .setLngLat([lon, lat])
        .setHTML(createOpenHousePopupHtml(primaryOpenHouse, openHouses));
      
      // Handle popup close
      popup.on('close', () => {
        if (
          isMobile &&
          originalMapViewRef.current &&
          mapRef.current &&
          !isPhotoModalOpenRef.current &&
          !suppressRestoreOnPopupCloseRef.current
        ) {
          mapRef.current.easeTo({
            center: originalMapViewRef.current.center,
            zoom: originalMapViewRef.current.zoom,
            duration: 500,
            essential: true
          });
          originalMapViewRef.current = null;
        }
        // Reset suppression flag if it was set for this programmatic close
        if (suppressRestoreOnPopupCloseRef.current) {
          suppressRestoreOnPopupCloseRef.current = false;
        }
        if (popupRef.current === popup) {
          popupRef.current = null;
        }
        // Always clear selection when popup closes to prevent blue markers
        if (selectedMarkerIdRef.current === markerId) {
          setSelectedMarkerId(null);
          selectedMarkerIdRef.current = null;
        }
        // Always update this marker's color (it may no longer be selected)
        updateMarkerColor(marker, markerId);
      });

      // Add hover event handlers
      marker.getElement().addEventListener('mouseenter', (e) => {
        e.stopPropagation();
        // Only set hover if no marker is currently selected
        if (!selectedMarkerId) {
          setHoveredMarkerId(markerId);
          updateMarkerColor(marker, markerId);
        }
      });

      marker.getElement().addEventListener('mouseleave', (e) => {
        e.stopPropagation();
        setHoveredMarkerId(null);
        // Don't update color here - let the useEffect handle it based on state change
      });

      // Add click and touch handlers to the marker to show the popup
      const handleMarkerInteraction = (e) => {
        e.stopPropagation();
        
        // Clear hover state when clicking
        setHoveredMarkerId(null);
        
        // Check if this marker is already selected (toggle behavior)
        const isCurrentlySelected = selectedMarkerId === markerId;
        
    // Track marker click
    analyticsService.trackMapInteraction('Marker Click', {
      propertyId: primaryOpenHouse?.listingId || markerId,
      address: primaryOpenHouse?.address
    });
        
        
        // Close any existing popup first
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }
        
        if (isCurrentlySelected) {
          // If clicking the same marker, deselect it and close popup
          setSelectedMarkerId(null);
          selectedMarkerIdRef.current = null;
          return; // Exit early - no popup needed
        }
        
    // Set this marker as selected
    setSelectedMarkerId(markerId);
    selectedMarkerIdRef.current = markerId; // Update ref immediately
        
        // Marker colors will be updated automatically by useEffect

        if (isMobile) {
          // Update marker color immediately for mobile
          updateMarkerColor(marker, markerId);
          
          const addPopupAfterPan = () => {
            if (mapRef.current) {
              if (!popup.isOpen()) {
                popupRef.current = popup;
                popup.addTo(mapRef.current);
                setupPopupPhotoClickListener(popup, primaryOpenHouse);
              }
              mapRef.current.off('moveend', addPopupAfterPan);
            }
          };

          if (mapRef.current) {
            originalMapViewRef.current = {
              center: mapRef.current.getCenter(),
              zoom: mapRef.current.getZoom()
            };
            mapRef.current.once('moveend', addPopupAfterPan);
            
            const mobileEstimatedHeaderHeight = 90;
            const mobilePopupAboveMarkerHeight = 0;
            const mobileTopBuffer = -10;

            const targetMarkerScreenY = mobileEstimatedHeaderHeight + mobilePopupAboveMarkerHeight + mobileTopBuffer;
            
            if (mapDomContainerRef.current) {
              const viewportHeight = mapDomContainerRef.current.clientHeight;
              const calculatedOffsetY = targetMarkerScreenY - (viewportHeight / 2);

              mapRef.current.easeTo({
                center: [lon, lat],
                offset: [0, calculatedOffsetY],
                duration: 500,
                essential: true
              });
            } else {
              mapRef.current.easeTo({
                center: [lon, lat],
                offset: [0, -150],
                duration: 500,
                essential: true
              });
            }
          }
        } else {
          // Not mobile, add popup immediately
          popupRef.current = popup;
          popup.addTo(map);
          setupPopupPhotoClickListener(popup, primaryOpenHouse);
          
          // Ensure the clicked marker stays blue after popup is added
          updateMarkerColor(marker, markerId);
        }
      };

      // Add event listeners based on device type to prevent duplicate events
      if (isMobile) {
        // On mobile, use touchend for better touch response
        marker.getElement().addEventListener('touchend', (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleMarkerInteraction(e);
        });
      } else {
        // On desktop, use click
        marker.getElement().addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleMarkerInteraction(e);
        });
      }

      marker.addTo(map);
      markersRef.current.push(marker);
    });
    
    // If we have a specific property we want to recenter to (e.g., after a day filter change)
    if (pendingMarkerIdRef.current) {
      const targetMarker = markersRef.current.find(m => m.getElement()?.dataset?.markerId === pendingMarkerIdRef.current);
      let lngLat = null;
      if (targetMarker && typeof targetMarker.getLngLat === 'function') {
        const ll = targetMarker.getLngLat();
        lngLat = { lng: ll.lng, lat: ll.lat };
      }

      // Fallback to previously captured center if we couldn't find the marker
      if (!lngLat && pendingCenterRef.current) {
        lngLat = { ...pendingCenterRef.current };
      }

      if (lngLat) {
        const { lng, lat } = lngLat;
      // Use a mobile-aware vertical offset so the popup area remains visible
      const mobileEstimatedHeaderHeight = 90;
      const mobilePopupAboveMarkerHeight = 0;
      const mobileTopBuffer = -10;
      const targetMarkerScreenY = mobileEstimatedHeaderHeight + mobilePopupAboveMarkerHeight + mobileTopBuffer;

      const doEaseToPendingCenter = () => {
        if (!mapRef.current) return;
        if (mapDomContainerRef.current) {
          const viewportHeight = mapDomContainerRef.current.clientHeight;
          const calculatedOffsetY = targetMarkerScreenY - (viewportHeight / 2);
          mapRef.current.easeTo({ center: [lng, lat], offset: [0, calculatedOffsetY], duration: 500, essential: true });
        } else {
          mapRef.current.easeTo({ center: [lng, lat], offset: [0, -150], duration: 500, essential: true });
        }
        // Clear the pending targets so it runs only once
        pendingCenterRef.current = null;
        pendingMarkerIdRef.current = null;
      };

      // Ensure we run after the map has rendered the new state
      if (typeof mapRef.current.isStyleLoaded === 'function' && mapRef.current.isStyleLoaded()) {
        requestAnimationFrame(doEaseToPendingCenter);
      } else if (typeof mapRef.current.once === 'function') {
        mapRef.current.once('idle', doEaseToPendingCenter);
      } else {
        // Fallback timing
        setTimeout(doEaseToPendingCenter, 0);
      }
      } else {
        // Nothing to recenter to
        pendingMarkerIdRef.current = null;
        pendingCenterRef.current = null;
      }
    } else if (pendingCenterRef.current) {
      const { lng, lat } = pendingCenterRef.current;
      // Use a mobile-aware vertical offset so the popup area remains visible
      const mobileEstimatedHeaderHeight = 90;
      const mobilePopupAboveMarkerHeight = 0;
      const mobileTopBuffer = -10;
      const targetMarkerScreenY = mobileEstimatedHeaderHeight + mobilePopupAboveMarkerHeight + mobileTopBuffer;

      const doEaseToPendingCenter = () => {
        if (!mapRef.current) return;
        if (mapDomContainerRef.current) {
          const viewportHeight = mapDomContainerRef.current.clientHeight;
          const calculatedOffsetY = targetMarkerScreenY - (viewportHeight / 2);
          mapRef.current.easeTo({ center: [lng, lat], offset: [0, calculatedOffsetY], duration: 500, essential: true });
        } else {
          mapRef.current.easeTo({ center: [lng, lat], offset: [0, -150], duration: 500, essential: true });
        }
        // Clear the pending center so it runs only once
        pendingCenterRef.current = null;
      };

      // Ensure we run after the map has rendered the new state
      if (typeof mapRef.current.isStyleLoaded === 'function' && mapRef.current.isStyleLoaded()) {
        requestAnimationFrame(doEaseToPendingCenter);
      } else if (typeof mapRef.current.once === 'function') {
        mapRef.current.once('idle', doEaseToPendingCenter);
      } else {
        // Fallback timing
        setTimeout(doEaseToPendingCenter, 0);
      }
    } else {
      // Otherwise, fit to bounds if the user hasn't interacted yet
      if (!bounds.isEmpty() && !hasUserInteracted) {
        const bottomPadding = isMobile ? 50 : 50;
        map.fitBounds(bounds, { 
          padding: { top: 50, bottom: bottomPadding, left: 60, right: 60 },
          maxZoom: 13,
          duration: 1000
        });
      }
    }
  }, [filteredOpenHouses, showModal, isMobile, setupPopupPhotoClickListener, hasUserInteracted]);

  // Set initial marker colors after markers are created
  useEffect(() => {
    markersRef.current.forEach((marker) => {
      const markerElement = marker.getElement();
      const markerId = markerElement.dataset.markerId;
      if (markerId) {
        updateMarkerColor(marker, markerId);
      }
    });
  }, [markersRef.current.length, updateMarkerColor]); // Only run when markers are created

  // Effect to update marker colors when any state changes
  useEffect(() => {
    markersRef.current.forEach((marker) => {
      const markerElement = marker.getElement();
      const markerId = markerElement.dataset.markerId;
      if (markerId) {
        updateMarkerColor(marker, markerId);
      }
    });
  }, [hoveredMarkerId, selectedMarkerId, activeModalListing, updateMarkerColor]);

  // Effect to handle map resizing using ResizeObserver
  useEffect(() => {
    const map = mapRef.current;
    const mapContainerElement = mapViewRootRef.current;

    if (!map || !mapContainerElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      map.resize();
    });

    resizeObserver.observe(mapContainerElement);

    requestAnimationFrame(() => {
      map.resize();
    });

    return () => {
      resizeObserver.unobserve(mapContainerElement);
      resizeObserver.disconnect();
    };
  }, []);

  const handleRecenterMap = () => {
    const map = mapRef.current;
    if (!map || !filteredOpenHouses || filteredOpenHouses.length === 0) {
      return;
    }

    // Close any open popup before recentering
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    // Clear selection and hover state so the selected pin returns to red immediately
    setSelectedMarkerId(null);
    selectedMarkerIdRef.current = null;
    setHoveredMarkerId(null);
    // Update all marker colors now that selection is cleared
    markersRef.current.forEach((marker) => {
      const markerElement = marker.getElement();
      const markerId = markerElement?.dataset?.markerId;
      if (markerId) {
        updateMarkerColor(marker, markerId);
      }
    });

    const bounds = new mapboxgl.LngLatBounds();
    let validPointsExist = false;
    filteredOpenHouses.forEach(openHouse => {
      const lat = parseFloat(openHouse.latitude);
      const lon = parseFloat(openHouse.longitude);
      if (!isNaN(lat) && !isNaN(lon)) {
        bounds.extend([lon, lat]);
        validPointsExist = true;
      }
    });

    if (validPointsExist) {
      const padding = { top: 50, bottom: 50, left: 60, right: 60 };
      map.fitBounds(bounds, { 
        padding: padding,
        maxZoom: 13, 
        duration: 1000 
      });
    }
  };

  // Show loading or error states
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        fontSize: '16px',
        color: '#666'
      }}>
        Loading demo open houses...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        fontSize: '16px',
        color: '#d32f2f'
      }}>
        Error loading demo data: {error.message}
      </div>
    );
  }

  if (!openHouses || openHouses.length === 0) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        fontSize: '16px',
        color: '#666'
      }}>
        No open houses in demo data
      </div>
    );
  }

  return (
    <>
      {/* Demo Modal */}
      <DemoModal 
        show={showDemoModal}
        onClose={closeDemoModal}
      />
      
      <div ref={mapViewRootRef} className="map-side" style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        <div 
          ref={mapDomContainerRef}
          id="open-house-map-view" 
          className="map-container"
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%'
          }}
        />
        {/* Map Controls */}
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          alignItems: 'flex-end'
        }}>
          {/* Day Filter Dropdown */}
          <DayFilterDropdown
            value={selectedDay}
            options={dayFilterOptions}
            onChange={(e) => {
              // If a marker is currently selected, capture its ID and coordinates for recenter after filter change
              const currentSelectedId = selectedMarkerIdRef.current;
              if (currentSelectedId) {
                pendingMarkerIdRef.current = currentSelectedId;
                const selectedMarker = markersRef.current.find(m => m.getElement()?.dataset?.markerId === currentSelectedId);
                if (selectedMarker && typeof selectedMarker.getLngLat === 'function') {
                  const ll = selectedMarker.getLngLat();
                  pendingCenterRef.current = { lng: ll.lng, lat: ll.lat };
                } else {
                  pendingCenterRef.current = null;
                }
              } else {
                pendingCenterRef.current = null;
                pendingMarkerIdRef.current = null;
              }

              setSelectedDay(e.target.value);
              // Clear selection/hover and close any open popup when day filter changes
              setSelectedMarkerId(null);
              selectedMarkerIdRef.current = null;
              setHoveredMarkerId(null);
              if (popupRef.current) {
                // Prevent restoring to pre-click camera for this programmatic close
                suppressRestoreOnPopupCloseRef.current = true;
                popupRef.current.remove();
                popupRef.current = null;
              }
              // Repaint all marker colors immediately
              markersRef.current.forEach((marker) => {
                const markerElement = marker.getElement();
                const markerId = markerElement?.dataset?.markerId;
                if (markerId) {
                  updateMarkerColor(marker, markerId);
                }
              });
            }}
            style={{ minWidth: '120px' }}
          />
          
          {/* Recenter Button */}
          <button
            onClick={e => { 
              handleRecenterMap(); 
              setTimeout(() => {
                if (e.currentTarget) {
                  e.currentTarget.blur();
                  e.currentTarget.style.outline = 'none';
                }
              }, 0);
            }}
            style={{
              background: 'white',
              border: '1px solid #dbdbdb',
              borderRadius: '4px',
              padding: '8px',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: '1',
              outline: 'none'
            }}
            onMouseDown={(e) => e.preventDefault()}
            title="Recenter Map"
          >
            <FaExpandArrowsAlt size={18} color="#333" />
          </button>
        </div>
      </div>
      
      {/* Photo Modal */}
      {activeModalListing && (
        <PhotoModalWithAgent
          listing={activeModalListing}
          onClose={hideModal}
          isMobile={isMobile}
        />
      )}

      {/* Agent Contact Modal */}
      {showAgentModal && (
        <AgentContactModal
          memberKey={selectedAgentKey}
          onClose={() => {
            setShowAgentModal(false);
            setSelectedAgentKey(null);
          }}
          show={showAgentModal}
          isMobile={isMobile}
        />
      )}
    </>
  );
}

export default DemoMapView;
